// src/components/Common/MapPicker.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Spinner } from 'reactstrap';
import { Navigation, Crosshair, Map } from 'react-feather';

interface MapPickerProps {
  latitude: number;
  longitude: number;
  radius: number;
  isEditing: boolean;
  onLocationChange?: (lat: number, lng: number) => void;
  height?: string;
}

const MapPicker: React.FC<MapPickerProps> = ({
  latitude,
  longitude,
  radius,
  isEditing,
  onLocationChange,
  height = '400px',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const circleRef = useRef<any>(null);
  const isEditingRef = useRef(isEditing);
  const onLocationChangeRef = useRef(onLocationChange);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    isEditingRef.current = isEditing;
  }, [isEditing]);

  useEffect(() => {
    onLocationChangeRef.current = onLocationChange;
  }, [onLocationChange]);

  // Inject Leaflet CSS once
  useEffect(() => {
    if (document.getElementById('leaflet-css')) return;
    const link = document.createElement('link');
    link.id = 'leaflet-css';
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);
  }, []);

  // Initialize map
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!containerRef.current || mapRef.current) return;
      const L = (await import('leaflet')).default;

      // Fix webpack icon paths
      delete (L.Icon.Default.prototype as any)._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
        iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
        shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
      });

      if (!alive || !containerRef.current) return;

      const map = L.map(containerRef.current, {
        center: [latitude || 20.5937, longitude || 78.9629],
        zoom: 16,
        zoomControl: true,
        attributionControl: true,
        dragging: true,
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19,
      }).addTo(map);

      // Custom teardrop marker
      const pinIcon = L.divIcon({
        className: '',
        html: `<div style="
          width:32px;height:32px;
          background:#0d6efd;border:3px solid #fff;
          border-radius:50% 50% 50% 0;transform:rotate(-45deg);
          box-shadow:0 4px 14px rgba(13,110,253,0.4);
          position:relative;
        "><div style="
          width:9px;height:9px;background:#fff;border-radius:50%;
          position:absolute;top:50%;left:50%;
          transform:translate(-50%,-50%) rotate(45deg);
        "></div></div>`,
        iconSize: [32, 32],
        iconAnchor: [16, 32],
      });

      const marker = L.marker([latitude || 20.5937, longitude || 78.9629], {
        icon: pinIcon,
        draggable: false,
        interactive: false,
      }).addTo(map);

      const circle = L.circle([latitude || 20.5937, longitude || 78.9629], {
        radius: radius || 100,
        color: '#0d6efd',
        fillColor: '#0d6efd',
        fillOpacity: 0.12,
        weight: 2,
        opacity: 0.55,
        interactive: false,
      }).addTo(map);

      // Click on map to move marker
      map.on('click', (e: any) => {
        if (!isEditingRef.current) return;
        const { lat, lng } = e.latlng;
        marker.setLatLng([lat, lng]);
        circle.setLatLng([lat, lng]);
        onLocationChangeRef.current?.(lat, lng);
      });

      mapRef.current = map;
      markerRef.current = marker;
      circleRef.current = circle;
      if (alive) setReady(true);
    })();

    return () => {
      alive = false;
    };
  }, []);

  // Sync props to map
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    const safeLat = isFinite(latitude) && latitude !== 0 ? latitude : 20.5937;
    const safeLng = isFinite(longitude) && longitude !== 0 ? longitude : 78.9629;
    const safeRad = isFinite(radius) && radius > 0 ? radius : 100;
    const pos: [number, number] = [safeLat, safeLng];
    markerRef.current?.setLatLng(pos);
    circleRef.current?.setLatLng(pos);
    circleRef.current?.setRadius(safeRad);
    mapRef.current.setView(pos, mapRef.current.getZoom(), { animate: true });
  }, [latitude, longitude, radius, ready]);

  // Update cursor in edit mode
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    const container = mapRef.current.getContainer() as HTMLElement;
    container.style.cursor = isEditing ? 'crosshair' : '';
    setTimeout(() => mapRef.current?.invalidateSize(), 150);
  }, [isEditing, ready]);

  return (
    <div className="position-relative w-100" style={{ height }}>
      <div ref={containerRef} className="w-100 h-100" style={{ zIndex: 0 }} />

      {isEditing && ready && (
        <div
          className="position-absolute top-0 start-50 translate-middle-x z-index-1 mt-2"
          style={{ zIndex: 999 }}
        >
          <span className="badge bg-primary px-3 py-2 shadow-lg">
            Click on the map to move the pin
          </span>
        </div>
      )}

      <div className="position-absolute bottom-0 start-0 m-2" style={{ zIndex: 999 }}>
        <div className="bg-white bg-opacity-90 border rounded-3 px-3 py-1.5 d-flex align-items-center gap-1 shadow-sm">
          <Map size={12} className="text-primary" />
          <span className="text-xs fw-bold text-secondary">{radius}m radius</span>
        </div>
      </div>

      {!ready && (
        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-light">
          <Spinner color="primary" size="sm" className="me-2" />
          <span>Loading map...</span>
        </div>
      )}
    </div>
  );
};

export default MapPicker;